import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment-timezone";
import { useMutation, useQuery } from "@apollo/client";

import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { moovsBlue, white } from "globals/design-system/colors";
import Loading from "components/shared/Loading";
import { ArrowLeftIcon, PlusIcon } from "globals/design-system/icons";
import {
  CREATE_DRIVER_SCHEDULE_SETTING_AND_EVENTS_MUTATION,
  LOAD_DRIVER_SCHEDULE_EVENTS,
} from "globals/graphql";
import { DriverAvailabilityCalendar } from "./DriverAvailabilityCalendar";
import { CurrentViewName } from "./DriverAvailabilityCalendar/DriverAvailabilityCalendar";
import { useSnackbar } from "globals/hooks/useSnackbar";
import AddEventDialog, {
  AddEventFormData,
} from "pages/DriverAvailabilityPage/DriverAvailabilityCalendar/components/AddEventDialog";
import { DeleteEventDialog } from "pages/DriverAvailabilityPage/DriverAvailabilityCalendar/components";
import {
  DriverScheduleSettingDaysToRepeatEnum,
  DriverScheduleEvent,
} from "types";
import { DriverScheduleSettingScheduleTypeEnum } from "types";
import { useAnalytics } from "globals/hooks/useAnalytics";
import { formatDateTimeForServer } from "globals/utils/dateFormatters";

const { Available } = DriverScheduleSettingScheduleTypeEnum;

const { Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday } =
  DriverScheduleSettingDaysToRepeatEnum;

const WEEKDAYS = [
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
];

function DriverAvailabilityPage() {
  // hooks
  const history = useHistory();
  const { driverId } = useParams<{ driverId: string }>();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const snackbar = useSnackbar();
  const { track } = useAnalytics();

  // state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentViewName, setCurrentViewName] =
    useState<CurrentViewName>("Month");
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] =
    useState<DriverScheduleEvent | null>(null);
  const [clickedCellDate, setClickedCellDate] = useState<Date | null>(null);

  // query
  const { data, loading, startPolling, stopPolling } = useQuery(
    LOAD_DRIVER_SCHEDULE_EVENTS,
    {
      variables: { driverId },
      fetchPolicy: "cache-and-network",
    }
  );

  const driverScheduleEvents = data?.loadDriverScheduleEvents;

  // mutations
  const [createDriverScheduleSettingAndEvents] = useMutation(
    CREATE_DRIVER_SCHEDULE_SETTING_AND_EVENTS_MUTATION,
    {
      onCompleted: (data) => {
        const result = data.createDriverScheduleSettingAndEvents.result;

        if (
          result.__typename === "CreateDriverScheduleSettingAndEventsFailure"
        ) {
          snackbar.error(result.reason);
          return;
        }

        if (result.driverScheduleSetting.scheduleType === Available) {
          track("avail_slot_created");
        } else {
          track("block_slot_created");
        }

        startPolling(1000);

        const timeout = setTimeout(() => {
          stopPolling();
        }, 3000);

        setPollingTimeout(timeout);

        snackbar.success("Event added successfully");

        handleAddEventDialogClose();
      },
    }
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [pollingTimeout]);

  // event handlers
  const handleBack = () => {
    history.push(`/driver/${driverId}`);
  };

  const handleAddEventDialogOpen = () => {
    setAddEventDialogOpen(true);
  };

  const handleAddEventDialogClose = () => {
    setAddEventDialogOpen(false);
    setClickedCellDate(null);
  };

  const handleDeleteEventDialogOpen = (event: DriverScheduleEvent) => {
    setSelectedEvent(event);
    setDeleteEventDialogOpen(true);
  };

  const handleDeleteEventDialogClose = () => {
    setDeleteEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleDateCellClick = (date: Date) => {
    setClickedCellDate(date);
    setAddEventDialogOpen(true);
  };

  const handleAddEventSubmit = (formData: AddEventFormData) => {
    let daysToRepeat: DriverScheduleSettingDaysToRepeatEnum[] | null = null;

    // format to save for server
    const adjustedStartDateTime = formatDateTimeForServer(
      formData.startDateTime
    );
    const adjustedEndDateTime = formatDateTimeForServer(formData.endDateTime);

    if (formData.repeatOption) {
      if (formData.repeatOption === "EVERYDAY") {
        daysToRepeat = WEEKDAYS;
      } else if (formData.repeatOption === "WEEKLY") {
        // Get start date's day of week from the original form data
        const startDate = new Date(formData.startDateTime);
        const dayOfWeek = WEEKDAYS[startDate.getDay()];
        daysToRepeat = [dayOfWeek];
      }
    }

    createDriverScheduleSettingAndEvents({
      variables: {
        input: {
          driverId: driverId,
          scheduleType: formData.eventType,
          startDateTime: new Date(adjustedStartDateTime),
          endDateTime: new Date(adjustedEndDateTime),
          daysToRepeat,
        },
      },
    });
  };

  if (loading || !driverScheduleEvents) {
    return <Loading />;
  }

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Header */}
      <Box
        borderBottom={`1px solid ${theme.palette.divider}`}
        bgcolor={theme.palette.background.paper}
      >
        <>
          <Box display={{ xs: "flex" }} flexDirection="column" p={2} gap={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              {/* back button */}
              <Box display="flex" alignItems="center" ml={isMobileView ? 1 : 2}>
                <Button onClick={handleBack} size="small" sx={{ ml: -1 }}>
                  <ArrowLeftIcon size="small" color={moovsBlue} />
                  <Typography
                    variant="body2"
                    color="primary"
                    ml={0.5}
                    fontWeight={500}
                  >
                    Back
                  </Typography>
                </Button>

                <Box ml={2}>
                  {isMobileView ? (
                    <Button
                      variant="contained"
                      onClick={handleAddEventDialogOpen}
                      sx={{
                        minWidth: 40,
                        width: 55,
                        height: 40,
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: 1,
                        bgcolor: moovsBlue,
                        "&:hover": {
                          bgcolor: moovsBlue,
                          opacity: 0.9,
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.3}>
                        Add
                        <PlusIcon size="xs" color={white} />
                      </Box>
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddEventDialogOpen}
                      sx={{
                        bgcolor: moovsBlue,
                        color: white,
                        "&:hover": {
                          bgcolor: moovsBlue,
                          opacity: 0.9,
                        },
                      }}
                    >
                      Add Slot
                    </Button>
                  )}
                </Box>
              </Box>

              {/* selected month and add slot */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant={isMobileView ? "h3" : "h1"}
                  fontWeight={600}
                  mr={1}
                >
                  {moment(selectedDate).format("MMM YYYY")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      </Box>

      {/* Driver Availability Calendar */}
      <Box flex={1} overflow="hidden" bgcolor="white" px={isMobileView ? 0 : 3}>
        <DriverAvailabilityCalendar
          selectedDate={selectedDate}
          driverScheduleEvents={driverScheduleEvents}
          onSelectedDateChange={setSelectedDate}
          currentViewName={currentViewName}
          onCurrentViewNameChange={setCurrentViewName}
          onEventClick={handleDeleteEventDialogOpen}
          onDateCellClick={handleDateCellClick}
        />
      </Box>

      {/* Add Event Dialog */}
      <AddEventDialog
        open={addEventDialogOpen}
        onClose={handleAddEventDialogClose}
        onSubmit={handleAddEventSubmit}
        clickedCellDate={clickedCellDate}
      />

      {/* Delete Event Dialog */}
      <DeleteEventDialog
        open={deleteEventDialogOpen}
        event={selectedEvent}
        onDeleteEventDialogClose={handleDeleteEventDialogClose}
        startPolling={startPolling}
        stopPolling={stopPolling}
      />
    </Box>
  );
}

export default DriverAvailabilityPage;
